import React from "react";
import { Card } from "../../components/ui/card";
import { Lock, Ban, ShieldCheck, Trash2 } from "lucide-react";
import Seo from "../../components/Seo";

const SECTIONS = [
  {
    icon: ShieldCheck,
    title: "Data Ownership & Control",
    body: "Your commitment data belongs to you. You can view, correct, export, or permanently delete your information at any time. We never claim ownership of the financial inputs you provide.",
  },
  {
    icon: Ban,
    title: "No Monetization & No Ads",
    body: "COMMITMENTT does not show advertising and does not sell, rent, or share your financial behaviour with data brokers or third-party advertisers. Your data is never a product.",
  },
  {
    icon: Lock,
    title: "Read-Only Financial Data Architecture",
    body: "COMMITMENTT is a read-only Income Shield. It has zero capability to move, withdraw, or transfer funds, and it never asks for your bank passwords or logins. There is no direct bank connection — you decide what to enter.",
  },
  {
    icon: Trash2,
    title: "Data Retention & One-Click Erasure",
    body: "You control retention. From your profile you can clear or delete all of your data with a single action, and it is removed from your account. We keep only what is needed to provide the service you asked for.",
  },
];

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-5 py-14">
      <Seo title="Privacy Policy" description="COMMITMENTT is a privacy-first Income Shield: no ads, no data-broker sharing, no bank credentials, and one-click data deletion." />
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-mint">Privacy Policy</span>
      <h1 className="font-heading text-4xl font-light text-navy mt-3">Your commitments are personal. Your data stays yours.</h1>
      <p className="text-slatey mt-4 leading-relaxed">
        COMMITMENTT is built privacy-first. This page explains, in plain language, how your information is handled.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        {SECTIONS.map((s) => (
          <Card key={s.title} className="rounded-2xl border-border shadow-card-sm p-5" data-testid={`privacy-section-${s.title.split(" ")[0].toLowerCase()}`}>
            <span className="h-10 w-10 rounded-xl bg-mint-light flex items-center justify-center">
              <s.icon size={18} strokeWidth={1.5} className="text-mint" />
            </span>
            <h3 className="font-heading text-lg font-medium text-navy mt-4">{s.title}</h3>
            <p className="text-sm text-slatey mt-2 leading-relaxed">{s.body}</p>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl border-border shadow-card-sm p-6 mt-6 space-y-4 text-slatey leading-relaxed">
        <p><strong className="text-ink">Uploads &amp; pasted messages.</strong> When you paste a message or upload a screenshot for AI-assisted extraction, the raw content is sent to our AI provider only to extract the details you requested, and is processed in memory — COMMITMENTT does not store the raw image or message on our servers or in the admin dashboard. We keep only the structured fields you review and confirm.</p>
        <p><strong className="text-ink">AI provider.</strong> AI-assisted extraction is performed by <strong className="text-ink">OpenAI</strong> (accessed via the Emergent LLM gateway), solely to extract the details you requested. OpenAI's handling of API data is governed by its own terms — see the{" "}
          <a href="https://openai.com/policies/row-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-mint underline underline-offset-4">OpenAI Privacy Policy</a>{" "}and{" "}
          <a href="https://openai.com/policies/usage-policies" target="_blank" rel="noopener noreferrer" className="text-mint underline underline-offset-4">API usage policies</a>. Per OpenAI's API policy, data submitted via the API is not used to train its models by default. COMMITMENTT does not use your financial information to train models.</p>
      </Card>

      <div className="rounded-2xl bg-secondary/70 border border-border p-5 mt-6">
        <p className="text-sm text-muted-foreground">
          <strong className="text-ink">Plain-language summary.</strong> This is a plain-language overview of our privacy approach, not formal legal advice.
          Please review and adapt it with a qualified professional before relying on it for a production launch.
        </p>
      </div>
    </div>
  );
}
